import type {ActionEnvelope, ChatAction, ChatState, StateAction} from '@microsoft/agent-host-protocol'
import {chatReducer} from '@microsoft/agent-host-protocol'
import type {ObservableRelaySink} from './relay-registry'

const ACTION_TOOL_CALL_START = 'chat/toolCallStart'
const ACTION_TOOL_CALL_READY = 'chat/toolCallReady'
const PART_TOOL_CALL = 'toolCall'

/**
 * Narrow a wire `StateAction` to the chat-scoped subset the reducer accepts.
 */
function isChatAction(action: StateAction): action is ChatAction {
  return action.type.startsWith('chat/')
}

function toolCallKey(action: StateAction): string | undefined {
  const toolCallId = (action as {toolCallId?: unknown}).toolCallId
  return typeof toolCallId === 'string' ? toolCallId : undefined
}

function hasToolCall(state: ChatState, toolCallId: string): boolean {
  const turns = [...(state.turns ?? []), ...(state.activeTurn ? [state.activeTurn] : [])]
  return turns.some(turn =>
    turn.responseParts.some(part => part.kind === PART_TOOL_CALL && part.toolCall.toolCallId === toolCallId),
  )
}

/**
 * Maintains the AHP `ChatState` for one relay session and exposes it
 * as an external store for `useSyncExternalStore`.
 */
export class AhpChatStore implements ObservableRelaySink {
  #state: ChatState | undefined
  #seeded = false
  #disposed = false
  // Work around copilotd occasionally publishing permission prompts
  // before their matching tool start. The observed wire order is:
  // `chat/toolCallReady` (confirmed absent, carries confirmation UI data) then
  // `chat/toolCallStart` for the same toolCallId. AHP's official chatReducer
  // intentionally ignores Ready unless the tool already exists in streaming or
  // running state, so without this buffer the UI renders a bare streaming tool
  // (often as "undefined") and never shows confirmation choices.
  //
  // Upstream copilotd places these on separate async paths:
  // - SDK `tool.execution_start` -> SessionPipelineEvent::Cli -> ChatToolCallStart
  // - permission callback -> SessionPipelineEvent::Permission -> ChatToolCallReady
  // The host should serialize these so Start reaches clients before Ready; once
  // that is fixed, this compatibility buffer can be removed.
  readonly #pendingToolReady = new Map<string, ChatAction>()
  readonly #listeners = new Set<() => void>()

  /**
   * No-op: the store owns its own state, so there is nothing to take ownership
   * of before events arrive. Present to satisfy the {@link RelaySessionSink}
   * contract the relay registry drives.
   */
  start(): void {}

  /** Seed (or re-seed, on reconnect) the state wholesale from a snapshot. */
  applySnapshot(state: ChatState): void {
    if (this.#disposed) return
    this.#state = state
    this.#seeded = true
    this.#pendingToolReady.clear()
    this.#emit()
  }

  /** Fold a single live action into the current state. */
  applyAction(env: ActionEnvelope): void {
    if (this.#disposed || !this.#seeded || !this.#state) return
    if (!isChatAction(env.action)) return

    if (env.action.type === ACTION_TOOL_CALL_READY) {
      const toolCallId = toolCallKey(env.action)
      if (toolCallId && !hasToolCall(this.#state, toolCallId)) {
        this.#pendingToolReady.set(toolCallId, env.action)
        return
      }
    }

    this.#state = chatReducer(this.#state, env.action)

    if (env.action.type === ACTION_TOOL_CALL_START) {
      const toolCallId = toolCallKey(env.action)
      const pendingReady = toolCallId ? this.#pendingToolReady.get(toolCallId) : undefined
      if (toolCallId && pendingReady) {
        this.#pendingToolReady.delete(toolCallId)
        this.#state = chatReducer(this.#state, pendingReady)
      }
    }

    this.#emit()
  }

  dispose(): void {
    this.#disposed = true
    this.#pendingToolReady.clear()
    this.#listeners.clear()
  }

  /** Current `ChatState`, or `undefined` before the first snapshot. */
  getSnapshot(): ChatState | undefined {
    return this.#state
  }

  /**
   * Register a listener fired on every state change. Returns an unsubscribe function.
   */
  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener)
    return () => this.#listeners.delete(listener)
  }

  #emit(): void {
    for (const listener of this.#listeners) listener()
  }
}
